"use client";

import dynamic from "next/dynamic";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";

const ContactDrawer = dynamic(() => import("./contact-drawer"), {
  ssr: false,
});

interface ContactDrawerContextType {
  openDrawer: () => void;
  closeDrawer: () => void;
  isOpen: boolean;
}

const ContactDrawerContext = createContext<ContactDrawerContextType | undefined>(
  undefined
);

type ContactDrawerSettings = {
  availableLabel?: string;
};

export function ContactDrawerProvider({
  children,
  initialSiteSettings,
}: {
  children: ReactNode;
  initialSiteSettings?: ContactDrawerSettings | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasMountedDrawer, setHasMountedDrawer] = useState(false);

  const openDrawer = () => setIsOpen(true);
  const closeDrawer = () => setIsOpen(false);

  useEffect(() => {
    if (isOpen && !hasMountedDrawer) {
      setHasMountedDrawer(true);
    }
  }, [isOpen, hasMountedDrawer]);

  return (
    <ContactDrawerContext.Provider value={{ openDrawer, closeDrawer, isOpen }}>
      {children}
      {hasMountedDrawer ? (
        <ContactDrawer
          open={isOpen}
          onOpenChange={setIsOpen}
          initialSiteSettings={initialSiteSettings}
        />
      ) : null}
    </ContactDrawerContext.Provider>
  );
}

export function useContactDrawer() {
  const context = useContext(ContactDrawerContext);
  // Return a no-op if not in provider context
  if (context === undefined) {
    return {
      openDrawer: () => {},
      closeDrawer: () => {},
      isOpen: false,
    };
  }
  return context;
}
